"use client";

import { useEffect, useRef, useState } from "react";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import { db, storage } from "@/lib/firebase";
import { collection, addDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { toTimestamp } from "@/lib/firestore";
import {
  applyMention,
  findActiveMention,
  resolveMentions,
  type MentionAnchor,
} from "@/lib/mentionParser";
import { MentionPicker } from "@/components/features/MentionPicker";
import type { MemberProfile } from "@/lib/models";

const MAX_IMAGES = 4;

interface CreatePostModalProps {
  onClose: () => void;
}

export function CreatePostModal({ onClose }: CreatePostModalProps) {
  const { householdId, profile, members } = useHouseholdContext();
  const t = useTranslations();
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [mentionAnchor, setMentionAnchor] = useState<MentionAnchor | null>(
    null,
  );
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Revoke object URLs when previews change or on unmount to avoid leaks.
  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  const handleAddImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setSelectedImages((prev) => {
      const next = [...prev, ...files].slice(0, MAX_IMAGES);
      setImagePreviews((oldPreviews) => {
        oldPreviews.forEach((u) => URL.revokeObjectURL(u));
        return next.map((f) => URL.createObjectURL(f));
      });
      return next;
    });
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const handleRemoveImage = (idx: number) => {
    setSelectedImages((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      setImagePreviews((oldPreviews) => {
        oldPreviews.forEach((u) => URL.revokeObjectURL(u));
        return next.map((f) => URL.createObjectURL(f));
      });
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !householdId || !profile?.uid) return;

    setSubmitting(true);
    setError("");

    try {
      let imageUrls: string[] = [];
      if (selectedImages.length > 0) {
        const { compressForContent } = await import("@/lib/imageCompressor");
        imageUrls = await Promise.all(
          selectedImages.map(async (file) => {
            const compressed = await compressForContent(file);
            const fileName = crypto.randomUUID();
            const imageRef = ref(
              storage,
              `posts/${householdId}/${fileName}.jpg`
            );
            await uploadBytes(imageRef, compressed);
            return await getDownloadURL(imageRef);
          })
        );
      }

      // Resolve `@nickname` tokens to member UIDs so the backend
      // `post_mention` push fires for each mentioned user. Mirrors iOS
      // `AddEditPostView.swift:241` (`MentionRenderer.resolveMentions`).
      const mentions = resolveMentions(message, members);
      const postsRef = collection(db, `households/${householdId}/posts`);
      await addDoc(postsRef, {
        post_title: title.trim(),
        post_message: message.trim() || null,
        created_by: profile.uid,
        created_at: toTimestamp(new Date()),
        images: imageUrls,
        mentions,
        num_likes: 0,
        num_replies: 0,
      });
      onClose();
    } catch (err) {
      console.error("Failed to create post:", err);
      setError(t("posts.compose.errorCreate"));
      setSubmitting(false);
    }
  };

  // Detect whether the cursor is currently inside an active `@mention`
  // token, and (re)open the picker accordingly. Runs on every change so the
  // picker live-filters as the user types.
  const refreshMentionAnchor = (text: string, cursor: number) => {
    setMentionAnchor(findActiveMention(text, cursor));
  };

  const handleMessageChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const next = e.target.value;
    setMessage(next);
    refreshMentionAnchor(next, e.target.selectionStart);
  };

  const handleSelectionChange = (
    e: React.SyntheticEvent<HTMLTextAreaElement>,
  ) => {
    refreshMentionAnchor(message, e.currentTarget.selectionStart);
  };

  const handleMentionSelect = (member: MemberProfile) => {
    if (!mentionAnchor) return;
    const cursor = textareaRef.current?.selectionStart ?? message.length;
    const { newText, newCursorIndex } = applyMention(
      message,
      cursor,
      member,
      mentionAnchor,
    );
    setMessage(newText);
    setMentionAnchor(null);
    // Restore the cursor position after React flushes the new value into the
    // DOM. setTimeout 0 is enough — we just need to hop past the current
    // microtask so the controlled input reflects `newText`.
    setTimeout(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(newCursorIndex, newCursorIndex);
    }, 0);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden text-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="text-blue-500 text-sm font-medium"
          >
            {t("posts.compose.cancel")}
          </button>
          <h2 className="font-bold text-base">{t("posts.compose.title")}</h2>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || submitting}
            className="text-sm font-semibold text-[color:var(--accent-color)] disabled:opacity-50"
          >
            {submitting ? t("posts.compose.posting") : t("posts.compose.post")}
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("posts.compose.titlePlaceholder")}
              className="w-full px-3 py-2 rounded-lg bg-gray-100 text-gray-900 placeholder-gray-400 text-base outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              autoFocus
            />
          </div>

          {/* Message + mention picker. The picker is anchored to this
              wrapper so it floats below the textarea regardless of content
              length. Mirrors iOS AddEditPostView L77-88 inline picker. */}
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleMessageChange}
              onKeyUp={handleSelectionChange}
              onClick={handleSelectionChange}
              onBlur={() => setMentionAnchor(null)}
              placeholder={t("posts.compose.messagePlaceholder")}
              rows={4}
              className="w-full px-3 py-2 rounded-lg bg-gray-100 text-gray-900 placeholder-gray-400 text-base outline-none resize-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
            {mentionAnchor && (
              <MentionPicker
                members={members}
                query={mentionAnchor.query}
                onSelect={handleMentionSelect}
                onClose={() => setMentionAnchor(null)}
              />
            )}
          </div>

          {/* Image picker — up to 4 images */}
          <div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                disabled={selectedImages.length >= MAX_IMAGES}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                Add photo
              </button>
              <span className="text-xs text-gray-400">
                {selectedImages.length}/{MAX_IMAGES}
              </span>
            </div>
            <input
              ref={imageInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleAddImages}
              className="hidden"
            />
            {imagePreviews.length > 0 && (
              <div className="mt-2 flex gap-2 overflow-x-auto">
                {imagePreviews.map((preview, idx) => (
                  <div key={idx} className="relative shrink-0">
                    <img
                      src={preview}
                      alt={`Selected ${idx + 1}`}
                      className="w-20 h-20 rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(idx)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-700 text-white text-xs flex items-center justify-center hover:bg-gray-800"
                      aria-label={`Remove image ${idx + 1}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}
        </form>
      </div>
    </div>
  );
}
